import React, { useState } from 'react';
import { Menu, Search, Settings, ChevronDown, User } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { LogoutMenuItem } from '../auth/LogoutButton';
import { getRoleBadgeColor } from '../../utils/auth';
import { ThemeToggle } from '../ThemeToggle';

export function Header({ onToggleSidebar }) {
  const { user, userDisplayName, userInitials, userRoleDisplayName } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);

  const handleUserMenuToggle = () => {
    setShowUserMenu(!showUserMenu);
  };

  const handleCloseUserMenu = () => {
    setShowUserMenu(false);
  };

  return (
    <header
      className="px-6 py-4 flex items-center justify-between sticky top-0 z-30 backdrop-blur-sm transition-all duration-200"
      style={{
        backgroundColor: 'var(--bg-navbar)',
        borderBottom: 'none',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08), 0 1px 3px rgba(0,0,0,0.05)'
      }}
    >
      <div className="flex items-center" style={{ gap: 'var(--space-4)' }}>
        {/* Sidebar toggle button for mobile */}
        <button
          className="lg:hidden p-2 rounded-lg transition-all duration-200 hover:scale-105 focus-ring header-icon-button"
          style={{
            backgroundColor: 'var(--bg-hover)',
            color: 'var(--header-icon-color)'
          }}
          onClick={onToggleSidebar}
        >
          <Menu size={20} />
        </button>
        <div
          className="text-xl font-semibold"
          style={{
            color: 'var(--header-text-primary)',
            fontWeight: 'var(--font-semibold)'
          }}
        >
          CabTrack Pro
        </div>
      </div>

      <div className="flex items-center" style={{ gap: 'var(--space-4)' }}>
        {/* Search bar */}
        <div className="relative hidden md:block">
          <Search
            className="absolute left-3 top-1/2 -translate-y-1/2"
            size={18}
            style={{ color: 'var(--header-icon-color)' }}
          />
          <input
            type="text"
            placeholder="Search..."
            className="pl-10 pr-4 py-2.5 text-sm focus-ring transition-all duration-200 search-input"
            style={{
              backgroundColor: 'var(--input-bg)',
              border: '1px solid var(--input-border)',
              borderRadius: 'var(--radius-lg)',
              color: 'var(--input-text)',
              width: '300px'
            }}
          />
        </div>

        {/* Theme Toggle */}
        <ThemeToggle />

        {/* Settings button */}
        <button
          className="p-2.5 rounded-lg transition-all duration-200 hover:scale-105 focus-ring header-icon-button"
          style={{
            backgroundColor: 'var(--bg-hover)',
            color: 'var(--header-icon-color)'
          }}
        >
          <Settings size={18} />
        </button>

        {/* User menu */}
        <div className="relative">
          <button
            onClick={handleUserMenuToggle}
            className="flex items-center gap-2 p-2 rounded-lg transition-colors user-menu-button"
            style={{
              backgroundColor: 'transparent',
              color: 'var(--text-primary)'
            }}
          >
            {/* User avatar */}
            <div className="w-9 h-9 rounded-full bg-indigo-600 flex items-center justify-center text-white font-medium">
              {user?.avatar ? (
                <img
                  src={user.avatar}
                  alt={userDisplayName}
                  className="w-full h-full rounded-full object-cover"
                />
              ) : (
                userInitials
              )}
            </div>

            {/* User info (hidden on mobile) */}
            <div className="hidden sm:block text-left">
              <div className="text-sm font-medium" style={{ color: 'var(--header-text-primary)' }}>
                {userDisplayName}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs" style={{ color: 'var(--header-text-secondary)' }}>
                  {userRoleDisplayName}
                </span>
                <span
                  className={`text-xs px-2 py-0.5 rounded-full font-medium ${getRoleBadgeColor(user?.role)}`}
                >
                  {user?.role?.toUpperCase()}
                </span>
              </div>
            </div>

            <ChevronDown
              size={16}
              className={`transition-transform ${showUserMenu ? 'rotate-180' : ''}`}
              style={{ color: 'var(--header-icon-color)' }}
            />
          </button>

          {/* User dropdown menu */}
          {showUserMenu && (
            <>
              {/* Backdrop */}
              <div
                className="fixed inset-0 z-9999"
                onClick={handleCloseUserMenu}
              />

              {/* Menu */}
              <div
                className="absolute right-0 mt-2 w-64 rounded-lg shadow-lg py-2 z-99999 user-dropdown-menu"
                style={{
                  backgroundColor: 'var(--bg-card)',
                  border: 'none',
                  boxShadow: '0 10px 25px rgba(0,0,0,0.15), 0 4px 6px rgba(0,0,0,0.1)'
                }}
              >
                {/* User info header */}
                <div
                  className="px-4 py-3 border-b user-info-header"
                  style={{ borderColor: 'var(--dropdown-border)' }}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-indigo-600 flex items-center justify-center text-white font-medium">
                      {user?.avatar ? (
                        <img
                          src={user.avatar}
                          alt={userDisplayName}
                          className="w-full h-full rounded-full object-cover"
                        />
                      ) : (
                        userInitials
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium truncate" style={{ color: 'var(--dropdown-text-primary)' }}>
                        {userDisplayName}
                      </div>
                      <div className="text-xs truncate" style={{ color: 'var(--dropdown-text-secondary)' }}>
                        {user?.email}
                      </div>
                      <div className="mt-1">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getRoleBadgeColor(user?.role)}`}>
                          {userRoleDisplayName}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Menu items */}
                <div className="py-1">
                  <button
                    onClick={handleCloseUserMenu}
                    className="w-full text-left px-4 py-2 text-sm flex items-center transition-colors duration-150 dropdown-menu-item"
                    style={{ color: 'var(--dropdown-text-secondary)' }}
                  >
                    <User className="h-4 w-4 mr-3" />
                    View Profile
                  </button>

                  <button
                    onClick={handleCloseUserMenu}
                    className="w-full text-left px-4 py-2 text-sm flex items-center transition-colors duration-150 dropdown-menu-item"
                    style={{ color: 'var(--dropdown-text-secondary)' }}
                  >
                    <Settings className="h-4 w-4 mr-3" />
                    Account Settings
                  </button>
                </div>

                {/* Logout */}
                <div
                  className="border-t py-1"
                  style={{ borderColor: 'var(--dropdown-border)' }}
                >
                  <LogoutMenuItem onClick={handleCloseUserMenu} />
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
}
